import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { loadDuplicatePairs } from '../lib/adminCounts';
import { loadCounts } from '../lib/healthCounts';
import { loadBuyLinkCounts } from '../lib/buyLinkCounts';
import InfoTip from '../components/InfoTip';

// ── Helpers ───────────────────────────────────────────────────────────────────
function timeAgo(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function todayStr() {
  return new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });
}

// ── Catalogue box ─────────────────────────────────────────────────────────────
// What share of the live catalogue a box is, as words. null when either number
// is unknown: an unknown share is not 0%.
export function shareOf(count, live) {
  if (typeof count !== 'number' || typeof live !== 'number' || live <= 0) return null;
  if (count === 0) return '0%';
  const pct = (count / live) * 100;
  if (pct < 0.1) return '<0.1%';
  return `${pct < 10 ? pct.toFixed(1) : Math.round(pct)}%`;
}

// The two documents the panel publishes (spec 0f: tos and privacy; the matching
// page is fixed copy in code, not a document).
const LEGAL_DOCS = [
  { key: 'tos',     name: 'Terms of Service' },
  { key: 'privacy', name: 'Privacy Policy' },
];

// Nothing records a draft state: the Legal Pages editor has one button, "Save &
// publish", and the table has only key, body and updated_at. So a document is
// still a draft when nothing has been saved for it yet, which is a missing row or
// a blank body.
export function draftLegalDocs(rows) {
  return LEGAL_DOCS.filter(d => {
    const row = (rows || []).find(r => r.key === d.key);
    return !row || !row.body || !row.body.trim();
  }).map(d => d.name);
}

// A box is a plain card with its own button, not a button around the card: the
// tooltip inside it is a control too, and a control cannot sit inside a button.
function CatalogueBox({ label, tip, value, sub, share, explain, goLabel, onGo, loading, accent, isMobile }) {
  return (
    <div style={{
      background: 'var(--brand-white)', border: '1px solid var(--brand-sand)',
      borderTop: `3px solid ${accent}`, borderRadius: 'var(--radius-lg)',
      padding: '18px 20px', display: 'flex', flexDirection: 'column', minWidth: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
          {label}
        </span>
        <InfoTip text={tip} width={240} />
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'var(--font-display)', fontSize: 30, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -1, lineHeight: 1 }}>
          {loading || value == null ? '—' : value.toLocaleString()}
        </span>
        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--neutral-400)' }}>
          {loading ? '' : share ? `${share} of live books` : 'share unknown'}
        </span>
      </div>
      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)', margin: '8px 0 0', lineHeight: 1.45 }}>{explain}</p>
      {sub && !loading && (
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: '6px 0 0', lineHeight: 1.4 }}>{sub}</p>
      )}
      <div style={{ marginTop: 'auto', paddingTop: 12 }}>
        <button
          type="button"
          onClick={onGo}
          style={{
            fontSize: 'var(--font-size-sm)', fontWeight: 600, padding: isMobile ? '0 14px' : '6px 14px',
            minHeight: isMobile ? 44 : undefined,
            borderRadius: 'var(--radius-md)', border: '1px solid var(--brand-sand)',
            background: 'var(--brand-white)', color: 'var(--neutral-600)',
            cursor: 'pointer', fontFamily: 'var(--font-body)',
          }}
        >
          {goLabel}
        </button>
      </div>
    </div>
  );
}

// ── Attention row ─────────────────────────────────────────────────────────────
function AttentionRow({ icon, title, sub, count, urgent, onGo }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 'var(--space-md)',
      padding: '12px 0',
      borderBottom: '1px solid var(--brand-cream)',
    }}>
      <div style={{
        width: 34, height: 34, borderRadius: 9, flexShrink: 0,
        background: urgent ? '#fff8f8' : '#fffbeb',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 'var(--font-size-lg)',
      }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--neutral-800)', marginBottom: 'var(--space-xxs)' }}>
          {title}
          {count > 0 && (
            <span style={{
              marginLeft: 'var(--space-sm)', fontSize: 'var(--font-size-xxs)', fontWeight: 700,
              padding: '2px 7px', borderRadius: 'var(--radius-10)',
              background: urgent ? 'var(--feedback-error-bg)' : 'var(--feedback-warning-bg)',
              color: urgent ? '#991b1b' : 'var(--feedback-warning-ink)',
            }}>
              {count}
            </span>
          )}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--neutral-500)' }}>{sub}</div>
      </div>
      <button
        onClick={onGo}
        style={{
          fontSize: 'var(--font-size-sm)', fontWeight: 600, padding: '6px 14px',
          borderRadius: 'var(--radius-md)', border: '1px solid var(--brand-sand)',
          background: 'var(--brand-white)', color: 'var(--neutral-600)',
          cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
          fontFamily: 'var(--font-body)',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = '#faf8f5'; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
      >
        Review →
      </button>
    </div>
  );
}

// ── Activity row ──────────────────────────────────────────────────────────────
function ActivityRow({ log }) {
  const iconMap = {
    create: { icon: '✅', bg: '#f0fdf4' },
    update: { icon: '✏️', bg: '#eff6ff' },
    delete: { icon: '🗑️', bg: '#fff8f8' },
    merge:  { icon: '🔀', bg: '#faf5ff' },
  };
  const meta = iconMap[log.action_type] || { icon: '📋', bg: '#faf8f5' };

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 'var(--space-10)',
      padding: '10px 0', borderBottom: '1px solid var(--brand-cream)',
    }}>
      <div style={{
        width: 30, height: 30, borderRadius: 'var(--radius-md)', flexShrink: 0,
        background: meta.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 'var(--font-size-md)',
      }}>
        {meta.icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 12.5, color: 'var(--neutral-800)', margin: 'var(--space-none)', lineHeight: 1.4 }}>
          {log.description || `${log.action_type} · ${log.entity_type}`}
        </p>
        <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: '2px 0 0' }}>
          {log.actor || 'admin'} · {timeAgo(log.created_at)}
        </p>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN EXPORT
// ══════════════════════════════════════════════════════════════════════════════
export default function OverviewSection({ onNavigate, adminUsername, isMobile }) {
  const [loading, setLoading]       = useState(true);
  const [catalogue, setCatalogue]   = useState({});
  const [legalNote, setLegalNote]   = useState(null);
  const [attention, setAttention]   = useState([]);
  const [activity, setActivity]     = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loadFailed, setLoadFailed]         = useState(false);
  const [activityFailed, setActivityFailed] = useState(false);
  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    setLoadFailed(false);
    setActivityFailed(false);
    try {
      const [
        cat,
        pendingBooksR,
        pendingTropesR,
        dupePairsR,
        openReportsR,
        flaggedEventsR,
        legalR,
        buy,
      ] = await Promise.all([
        // The catalogue boxes and the embedding row. The same filters Health's
        // tabs use (src/admin/lib/healthCounts.js), so a box and the tab it opens
        // cannot disagree.
        loadCounts(['live', 'ready', 'nodata', 'stale', 'notSearchableNodata', 'notSearchableStale', 'noauthor', 'readerCopy'], 'Overview'),
        supabase.from('books').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('tropes').select('*', { count: 'exact', head: true }).eq('is_active', false).in('merge_status', ['active', 'pending_review']),
        // The same count the sidebar badge and the Duplicates tab use.
        loadDuplicatePairs(),
        supabase.from('reports').select('*', { count: 'exact', head: true }).eq('status', 'open'),
        supabase.from('profile_events').select('*', { count: 'exact', head: true }).eq('flagged', true),
        supabase.from('legal_documents').select('key, body').in('key', LEGAL_DOCS.map(d => d.key)),
        // Live books that show no Buy button: counted in the database by the rule the reader
        // app uses (ticket 48), the same call the Affiliate Links screen makes. null = could not read.
        loadBuyLinkCounts('Overview'),
      ]);

      // A count whose read was refused is null, never 0. A 0 reads as "nothing
      // waiting", which is exactly what a database that refused every read would
      // otherwise have told the admin: "All clear", on every card.
      const n = (r) => (r && !r.error && typeof r.count === 'number' ? r.count : null);
      const pendingBooks     = n(pendingBooksR);
      const pendingTropes    = n(pendingTropesR);
      const dupeTropes       = dupePairsR.ok ? dupePairsR.pairs.length : null;
      const openReports      = n(openReportsR);
      const flaggedEvents    = n(flaggedEventsR);
      const c = cat.counts;

      const countsFailed = cat.failed > 0 || [
        pendingBooks, pendingTropes, dupeTropes, openReports, flaggedEvents, buy === null ? null : buy.nowhere,
      ].some(v => v === null);
      if (countsFailed) {
        console.error('Overview: at least one count could not be read');
        setLoadFailed(true);
      }

      setCatalogue(c);

      // "Still a draft" is a missing or blank document (see draftLegalDocs). A
      // read that failed must not read as "all published".
      if (legalR.error) {
        console.error('Overview: legal documents read failed:', legalR.error.message);
        setLegalNote({ failed: true });
      } else {
        const drafts = draftLegalDocs(legalR.data);
        setLegalNote(drafts.length ? { drafts } : null);
      }

      // Rows for a count that failed are left out: they would show a figure we do not have.
      const items = [];
      if ((pendingBooks || 0) > 0) items.push({
        id: 'books', icon: '📚',
        title: 'Books to review',
        sub: `${pendingBooks} submission${pendingBooks !== 1 ? 's' : ''} awaiting publish`,
        count: pendingBooks, urgent: false, section: 'queue',
      });
      if ((openReports || 0) > 0) items.push({
        id: 'reports', icon: '🚨',
        title: 'Open reports',
        sub: `Community reports on posts, books, and users`,
        count: openReports, urgent: (openReports || 0) >= 3, section: 'reports',
      });
      // Health's two states, not "failed": nothing records a failed embedding.
      // Left out when EITHER count failed: with one missing, the other would be
      // shown as the whole. This row uses Health's full counts because it opens
      // those tabs.
      const embedTotal = c.nodata == null || c.stale == null ? null : c.nodata + c.stale;
      if (embedTotal != null && embedTotal > 0) items.push({
        id: 'embeddings', icon: '🩺',
        title: 'Matching data to fix',
        sub: `${c.nodata} with no matching data yet · ${c.stale} stale`,
        count: embedTotal, urgent: false,
        section: c.nodata > 0 ? 'health?tab=nodata' : 'health?tab=stale',
      });
      // Live books with nowhere to buy them. The count is the database's, applying isBookLink, so
      // a book with a link readers cannot use is in it. Left out when the count could not be read.
      if (buy && buy.nowhere > 0) items.push({
        id: 'buy', icon: '🛒',
        title: 'Live books with nowhere to buy them',
        sub: `${buy.noLink.toLocaleString()} with no link saved · ${buy.rejected.toLocaleString()} with links readers can't use`,
        count: buy.nowhere, urgent: false, section: 'affiliate',
      });
      // Same rule: pending tags + duplicate pairs is only a total when both read.
      const tropeTotal = pendingTropes == null || dupeTropes == null ? null : pendingTropes + dupeTropes;
      if (tropeTotal != null && tropeTotal > 0) items.push({
        id: 'tropes', icon: '🏷️',
        title: 'Trope queue',
        sub: `${pendingTropes} pending approval · ${dupeTropes} duplicate pairs`,
        count: tropeTotal, urgent: false, section: 'c-trope-queue',
      });
      if ((flaggedEvents || 0) > 0) items.push({
        id: 'events', icon: '📍',
        title: 'Flagged events',
        sub: `Events flagged by community members`,
        count: flaggedEvents, urgent: false, section: 'c-events',
      });

      setAttention(items);
      setTotalItems(items.reduce((a, b) => a + b.count, 0));

      const { data: logs, error: logsErr } = await supabase
        .from('admin_audit_log')
        .select('id, actor, action_type, entity_type, description, created_at')
        .order('created_at', { ascending: false })
        .limit(8);

      if (logsErr) {
        console.error('Overview: audit log read failed:', logsErr.message);
        setActivityFailed(true);
        setActivity([]);
      } else {
        setActivity(logs || []);
      }
    } catch (err) {
      console.error('Overview load error:', err.message);
      setLoadFailed(true);
      setActivityFailed(true);
    }
    setLoading(false);
  }

  // The middle box is both of Health's first two tabs. It opens the first one that
  // has anything in it.
  // The middle box counts LIVE books only (spec section 3), so it can read lower
  // than the Matching data to fix row, which uses Health's full counts.
  const notSearchable = catalogue.notSearchableNodata == null || catalogue.notSearchableStale == null ? null : catalogue.notSearchableNodata + catalogue.notSearchableStale;
  const notSearchableGo = (catalogue.notSearchableNodata || 0) > 0 ? 'health?tab=nodata' : (catalogue.notSearchableStale || 0) > 0 ? 'health?tab=stale' : 'health';

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '30px 36px', fontFamily: 'var(--font-body)' }}>

      {/* ── Header ── */}
      <div style={{
        display: 'flex', alignItems: 'flex-start',
        justifyContent: 'space-between', marginBottom: 26, gap: 'var(--space-md)',
        flexWrap: 'wrap',
      }}>
        <div>
          <h1 style={{
            fontFamily: 'var(--font-display)', fontSize: isMobile ? 20 : 26,
            fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -0.6,
            lineHeight: 1.1, margin: 'var(--space-none)',
          }}>
            {greeting()}{adminUsername ? `, ${adminUsername}` : ''}
          </h1>
          <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', marginTop: 'var(--space-xs)' }}>
            {todayStr()}
            {!loading && loadFailed && (
              <> · <span style={{ color: 'var(--feedback-error)', fontWeight: 600 }}>Couldn't load some counts</span></>
            )}
            {!loading && !loadFailed && totalItems > 0 && (
              <> · <span style={{ color: 'var(--feedback-error)', fontWeight: 600 }}>{totalItems} item{totalItems !== 1 ? 's' : ''} need your attention</span></>
            )}
            {!loading && !loadFailed && totalItems === 0 && (
              <> · <span style={{ color: '#16a34a', fontWeight: 600 }}>✓ All clear</span></>
            )}
          </p>
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 7,
          fontSize: 'var(--font-size-sm)', fontWeight: 600, color: '#065f46',
          background: '#d1fae5', border: '1px solid #a7f3d0',
          borderRadius: 'var(--radius-xxl)', padding: '6px 14px', flexShrink: 0,
        }}>
          <div style={{ width: 7, height: 7, borderRadius: 'var(--radius-circle)', background: '#16a34a', boxShadow: '0 0 0 2px rgba(22,163,74,0.3)' }} />
          Platform live
          {/* Nothing checks this: it is a fixed label, not a status. */}
          <span style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, letterSpacing: 0.5, padding: '2px 6px', borderRadius: 'var(--radius-md)', border: '1px solid var(--feedback-warning-border)', color: 'var(--feedback-warning-ink)', background: 'var(--brand-white)' }}>NOT WIRED</span>
        </div>
      </div>

      {/* ── Policy note: one line, gates nothing (spec section 3) ── */}
      {!loading && legalNote && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-md)', flexWrap: 'wrap',
          fontSize: 'var(--font-size-sm)', color: 'var(--feedback-warning-ink)', background: 'var(--feedback-warning-bg)',
          border: '1px solid var(--feedback-warning-border)', borderRadius: 'var(--radius-md)', padding: '8px 12px',
          marginBottom: 'var(--space-lg)', lineHeight: 1.5,
        }}>
          <span>
            {legalNote.failed
              ? "Couldn't check whether the legal documents are published."
              : `${legalNote.drafts.join(' and ')} ${legalNote.drafts.length === 1 ? 'is' : 'are'} still a draft: nothing has been published from Legal Pages yet.`}
          </span>
          <button
            type="button"
            onClick={() => onNavigate?.('c-legal')}
            style={{
              fontSize: 'var(--font-size-sm)', fontWeight: 600, padding: isMobile ? '0 14px' : '4px 12px', minHeight: isMobile ? 44 : undefined,
              borderRadius: 'var(--radius-md)', border: '1px solid var(--feedback-warning-border)',
              background: 'var(--brand-white)', color: 'var(--feedback-warning-ink)', cursor: 'pointer', fontFamily: 'var(--font-body)',
            }}
          >
            Legal Pages →
          </button>
        </div>
      )}

      {/* ── The catalogue: three states a live book can be in. No stat cards (spec 0f). ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
        gap: isMobile ? 10 : 14,
        marginBottom: 'var(--space-xxl)',
      }}>
        <CatalogueBox
          label="Ready for readers"
          tip="Live books with an author description whose matching data is current."
          value={catalogue.ready} share={shareOf(catalogue.ready, catalogue.live)} loading={loading} accent="#16a34a" isMobile={isMobile}
          explain="Live, described by the author, and searchable."
          goLabel="See Health →" onGo={() => onNavigate?.('health')}
        />
        <CatalogueBox
          label="Saved but not searchable"
          tip="Live books with an author description whose matching data is missing (No matching data yet) or out of date (Stale). Health's first two tabs can show more: books that aren't live yet, and stale books with no author description. A stale book still matches, on its old text."
          value={notSearchable} share={shareOf(notSearchable, catalogue.live)} loading={loading} accent="#d97706" isMobile={isMobile}
          explain="The description saved and its matching data is missing or out of date. A stale book still matches, on its old text."
          sub={catalogue.notSearchableNodata == null || catalogue.notSearchableStale == null ? "couldn't load the split" : `${catalogue.notSearchableNodata.toLocaleString()} with no matching data yet · ${catalogue.notSearchableStale.toLocaleString()} stale`}
          goLabel="Fix in Health →" onGo={() => onNavigate?.(notSearchableGo)}
        />
        <CatalogueBox
          label="No author description"
          tip="Live books with no author-written description, so they can't be a swipe card. They can still turn up under a trope tap and sit on the popular side of a pipeline. The second figure counts how many of them have a reader's copy saved."
          value={catalogue.noauthor} share={shareOf(catalogue.noauthor, catalogue.live)} loading={loading} accent="#2563eb" isMobile={isMobile}
          explain="No author description, so it can't be a swipe card."
          sub={catalogue.readerCopy == null ? "couldn't load the reader-copy count" : `${catalogue.readerCopy.toLocaleString()} of them have a reader's copy`}
          goLabel="See them in Health →" onGo={() => onNavigate?.('health?tab=noauthor')}
        />
      </div>

      {/* ── Two column body ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: 'var(--space-lg)',
      }}>

        {/* LEFT — Needs you */}
        <div style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: '20px 22px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-lg)' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>
              Needs you
            </h2>
            {attention.length > 0 && (
              <span style={{ fontSize: 10.5, color: 'var(--neutral-400)' }}>{attention.length} queue{attention.length !== 1 ? 's' : ''}</span>
            )}
          </div>

          {loading ? (
            <div style={{ padding: '30px 0', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading...</div>
          ) : attention.length === 0 && loadFailed ? (
            <div style={{ padding: '30px 0', textAlign: 'center' }}>
              <p style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 4px' }}>Couldn't load the queues</p>
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>Some counts could not be read, so this screen cannot say whether anything is waiting. Reload to try again.</p>
            </div>
          ) : attention.length === 0 ? (
            <div style={{ padding: '30px 0', textAlign: 'center' }}>
              <div style={{ fontSize: 'var(--font-size-h2)', marginBottom: 'var(--space-10)' }}>✅</div>
              <p style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 4px' }}>All queues clear</p>
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>Nothing needs your attention right now.</p>
            </div>
          ) : (
            <div>
              {loadFailed && (
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--feedback-warning-ink)', background: 'var(--feedback-warning-bg)', border: '1px solid var(--feedback-warning-border)', borderRadius: 'var(--radius-md)', padding: '8px 12px', margin: '0 0 10px', lineHeight: 1.5 }}>
                  Some counts could not be read, so this list may be missing queues.
                </p>
              )}
              {attention.map((item, idx) => (
                <div key={item.id} style={{ borderBottom: idx === attention.length - 1 ? 'none' : '1px solid var(--brand-cream)' }}>
                  <AttentionRow
                    icon={item.icon} title={item.title} sub={item.sub}
                    count={item.count} urgent={item.urgent}
                    onGo={() => onNavigate?.(item.section)}
                  />
                </div>
              ))}
            </div>
          )}

        </div>

        {/* RIGHT — Recent activity */}
        <div style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: '20px 22px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-lg)' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>
              Recent admin activity
            </h2>
            <span style={{ fontSize: 10.5, color: 'var(--neutral-400)' }}>live feed</span>
          </div>

          {loading ? (
            <div style={{ padding: '30px 0', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading...</div>
          ) : activityFailed ? (
            <div style={{ padding: '30px 0', textAlign: 'center' }}>
              <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--feedback-error)', margin: 'var(--space-none)' }}>
                Couldn't load recent activity. Reload to try again.
              </p>
            </div>
          ) : activity.length === 0 ? (
            <div style={{ padding: '30px 0', textAlign: 'center' }}>
              <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)', margin: 'var(--space-none)', fontStyle: 'italic' }}>
                No admin actions yet, they'll appear here as you review queues.
              </p>
            </div>
          ) : (
            <div>
              {activity.map((log, idx) => (
                <div key={log.id} style={{ borderBottom: idx === activity.length - 1 ? 'none' : undefined }}>
                  <ActivityRow log={log} />
                </div>
              ))}
            </div>
          )}

          {!loading && (
            <div style={{ marginTop: 'var(--space-lg)', paddingTop: 'var(--space-14)', borderTop: '1px solid var(--brand-cream)' }}>
              <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 'var(--space-10)' }}>
                Quick jump
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-6)' }}>
                {[
                  { label: '🏷️ Trope queue', section: 'c-trope-queue' },
                  { label: '🩺 Health',       section: 'health' },
                  { label: '📚 Books',        section: 'r-books' },
                  { label: '📍 Events',       section: 'c-events' },
                  { label: '👤 Users',        section: 'users' },
                  { label: '🚨 Reports',      section: 'reports' },
                  { label: '📋 Audit log',    section: 'audit' },
                ].map(q => (
                  <button
                    key={q.section}
                    onClick={() => onNavigate?.(q.section)}
                    style={{
                      fontSize: 11.5, fontWeight: 600, padding: '5px 12px',
                      borderRadius: 'var(--radius-lg)', border: '1px solid var(--brand-sand)',
                      background: 'var(--brand-white)', color: 'var(--neutral-600)',
                      cursor: 'pointer', fontFamily: 'var(--font-body)',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#faf8f5'; e.currentTarget.style.borderColor = '#d6d3d1'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e8e4dc'; }}
                  >
                    {q.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}